import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Send, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [rec, setRec] = useState(null);
  const endRef = useRef(null);
  const sessionId = useRef(`flyn_session_${Date.now()}`);
  const msgIdRef = useRef(0);
  const nextId = () => ++msgIdRef.current;

  useEffect(() => {
    (async () => {
      try {
        const g = await api.get("/flyn/greeting");
        setMessages([{ id: nextId(), role: "assistant", text: g.data.message }]);
      } catch (e) {
        console.warn("[coach] greeting failed:", e?.message);
        setMessages([{ id: nextId(), role: "assistant", text: "FLYN couldn't read your data right now. Ask me anything." }]);
      }
      try {
        const rr = await api.get("/supplements/recommendation/today");
        setRec(rr.data);
      } catch (e) {
        console.warn("[coach] recommendation failed:", e?.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", text: input };
    setMessages((m) => [...m, userMsg, { role: "assistant", text: "" }]);
    const sent = input;
    setInput("");
    setStreaming(true);

    try {
      const token = localStorage.getItem("fslab_session_token");
      const resp = await fetch(`${API}/flyn/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message: sent, session_id: sessionId.current }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          const line = ev.replace(/^data:\s*/, "");
          if (!line) continue;
          try {
            const data = JSON.parse(line);
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, text: last.text + data.delta };
                return copy;
              });
            }
          } catch (parseErr) {
            console.warn("[coach] SSE parse error:", parseErr?.message);
          }
        }
      }
    } catch (e) {
      console.warn("[coach] stream failed:", e?.message);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { id: copy[copy.length - 1].id, role: "assistant", text: "FLYN is unavailable. Try again in a moment." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14 flex flex-col" style={{ minHeight: "calc(100vh - 100px)" }} data-testid="coach-page">
      <div className="mb-6">
        <div className="eyebrow">FLYN · AI COACH</div>
        <h1 className="font-display text-5xl md:text-6xl uppercase mt-2 leading-none">Talk to your data.</h1>
      </div>

      <div className="flex-1 panel rounded-2xl p-6 md:p-8 mb-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
        {messages.map((m, i) => (
          <div key={m.id ?? `msg-${i}`} className={`mb-5 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" ? (
              <div className="flex items-start gap-3 max-w-[80%]" data-testid={`flyn-msg-${i}`}>
                <div className="w-8 h-8 rounded-full bg-obsidian text-lime flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="rounded-2xl bg-white border border-neutral-200 px-5 py-3 text-neutral-800 leading-relaxed">
                  {m.text || <span className="text-neutral-400">…</span>}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl bg-neutral-100 px-5 py-3 max-w-[75%] text-neutral-800" data-testid={`user-msg-${i}`}>
                {m.text}
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex items-center gap-3 border border-neutral-300 rounded-full pl-6 pr-2 py-2 bg-white">
        <input
          data-testid="coach-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={streaming ? "FLYN is reading…" : "Ask FLYN about today's data, recovery, sleep…"}
          className="flex-1 outline-none bg-transparent font-body text-base"
          disabled={streaming}
        />
        <button onClick={send} disabled={streaming || !input.trim()} className="btn-lime !py-2.5 !px-4 !text-[10px]" data-testid="coach-send">
          <Send className="w-4 h-4" />
        </button>
      </div>

      {rec?.product && (
        <div className="mt-8" data-testid="coach-rec-row">
          <div className="eyebrow mb-3">SIGNAL FROM YOUR DATA</div>
          <div className="panel p-5 rounded-2xl flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center font-display text-2xl" style={{ backgroundColor: rec.product.color, color: rec.product.id === "baseline" ? "#fff" : "#0A0A0A" }}>
                {rec.product.name[0]}
              </div>
              <div>
                <div className="font-display text-2xl leading-none">{rec.product.name}</div>
                <div className="text-sm text-neutral-600 mt-1">{rec.recommendation?.reasoning}</div>
              </div>
            </div>
            <Link to="/app/shop" className="btn-outline-ink">View →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
